(function () {
  if (customElements.get('cart-recs')) return;

  class CartRecs extends HTMLElement {
    static get observedAttributes() {
      return ['data-product-id', 'data-manual-handles'];
    }

    connectedCallback() {
      this._list = this.querySelector('.cart-recs__list');
      if (!this._list) return;

      this._list.addEventListener('click', (e) => {
        const addBtn = e.target.closest('.cart-recs__add');
        if (addBtn && !addBtn.disabled) this._addToCart(addBtn);

        const sizeBtn = e.target.closest('.cart-recs__size');
        if (sizeBtn && !sizeBtn.classList.contains('cart-recs__size--unavailable')) {
          const item = sizeBtn.closest('.cart-recs__item');
          if (!item) return;
          item.querySelectorAll('.cart-recs__size').forEach(b => b.classList.remove('cart-recs__size--selected'));
          sizeBtn.classList.add('cart-recs__size--selected');
          const cartBtn = item.querySelector('.cart-recs__add');
          if (cartBtn) cartBtn.dataset.variantId = sizeBtn.dataset.variantId;
        }
      });

      // Watch for morphSection clearing the list without replacing the element
      this._mutating = false;
      this._observer = new MutationObserver(() => {
        if (this._mutating) return;
        if (this._list && !this._list.children.length) {
          this._render(this.dataset.productId);
        }
      });
      this._observer.observe(this._list, { childList: true });

      this._render(this.dataset.productId);
    }

    disconnectedCallback() {
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }
    }

    // Fires when data-product-id or data-manual-handles is updated in place (morphSection attribute update)
    attributeChangedCallback(_name, oldValue, newValue) {
      if (oldValue !== null && newValue !== oldValue) {
        this._list = this.querySelector('.cart-recs__list');
        this._render(this.dataset.productId);
      }
    }

    async _render(productId) {
      if (!this._list) return;

      const manualHandles = this.dataset.manualHandles || '';
      const cacheKey = `cart-recs-v4:${manualHandles || productId || 'fallback'}`;

      // Show from cache instantly if available
      try {
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) {
          this._mutating = true;
          this._list.innerHTML = cached;
          this._mutating = false;
          this.hidden = false;
          return;
        }
      } catch (_) {}

      // Only hide while fetching if there's nothing to show yet
      if (!this._list.children.length) this.hidden = true;

      const products = await this._fetchProducts(productId);

      // Re-query after async gap in case DOM moved
      this._list = this.querySelector('.cart-recs__list');
      if (!this._list) return;

      if (!products.length) return;

      const items = [];
      for (const product of products) {
        try { items.push(this._buildItem(product)); } catch (_) {}
      }
      if (!items.length) return;

      const html = items.join('');
      try { sessionStorage.setItem(cacheKey, html); } catch (_) {}

      this._mutating = true;
      this._list.innerHTML = html;
      this._mutating = false;
      this.hidden = false;
    }

    async _fetchProducts(productId) {
      const manualHandles = (this.dataset.manualHandles || '')
        .split(',').map(s => s.trim()).filter(Boolean);

      if (manualHandles.length) {
        try {
          const fetched = await Promise.all(
            manualHandles.slice(0, 3).map(h =>
              fetch(`/products/${h}.js`).then(r => r.ok ? r.json() : null).catch(() => null)
            )
          );
          const valid = fetched.filter(Boolean);
          if (valid.length) return valid;
        } catch (_) {}
      }

      if (productId) {
        try {
          const res = await fetch(`/recommendations/products.json?product_id=${productId}&limit=6&intent=related`);
          if (res.ok) {
            const { products = [] } = await res.json();
            const filtered = products.filter(p => String(p.id) !== String(productId)).slice(0, 3);
            if (filtered.length) return filtered;
          }
        } catch (_) {}
      }

      // Always fall back so something is always shown
      try {
        const res = await fetch('/products.json?limit=8');
        if (res.ok) {
          const { products = [] } = await res.json();
          return products.filter(p => String(p.id) !== String(productId)).slice(0, 3);
        }
      } catch (_) {}

      return [];
    }

    _buildItem(product) {
      const variants = product.variants || [];
      if (!variants.length) throw new Error('no variant');

      const defaultVariant = variants.find(v => v.available) || variants[0];
      const price = this._formatMoney(defaultVariant.price);

      const fi = product.featured_image;
      const imgUrl = fi ? (typeof fi === 'string' ? fi : fi.src) : null;
      const imgSrc = imgUrl ? this._resizeImg(imgUrl, 300) : null;
      const img = imgSrc
        ? `<img src="${imgSrc}" alt="${this._esc(product.title)}" class="cart-recs__img" loading="lazy" width="300" height="400">`
        : `<div class="cart-recs__img-placeholder"></div>`;

      const sizeGroups = this._getSizeGroups(product, variants);
      let sizesHtml = '';
      if (sizeGroups.length > 0) {
        const circles = sizeGroups.map(({ size, variantId, available }) => {
          const selected = variantId === defaultVariant.id;
          return `<button type="button" class="cart-recs__size${selected ? ' cart-recs__size--selected' : ''}${!available ? ' cart-recs__size--unavailable' : ''}" data-variant-id="${variantId}" aria-label="${this._esc(size)}">${this._esc(size)}</button>`;
        }).join('');
        sizesHtml = `<div class="cart-recs__sizes">${circles}</div>`;
      }

      return `
        <div class="cart-recs__item">
          <a href="/products/${product.handle}" class="cart-recs__img-link" tabindex="-1" aria-hidden="true">${img}</a>
          <div class="cart-recs__info">
            <a href="/products/${product.handle}" class="cart-recs__name">${this._esc(product.title)}</a>
            <span class="cart-recs__price">${price}</span>
            ${sizesHtml}
            <button type="button" class="cart-recs__add" data-variant-id="${defaultVariant.id}" ${!defaultVariant.available ? 'disabled' : ''}>${defaultVariant.available ? 'ADD TO CART' : 'Sold out'}</button>
          </div>
        </div>`;
    }

    _getSizeGroups(product, variants) {
      const options = product.options || [];
      const sizeIdx = options.findIndex(o => /size/i.test(o.name || o));
      if (sizeIdx < 0) return [];
      const key = `option${sizeIdx + 1}`;
      const seen = new Set();
      const groups = [];
      for (const v of variants) {
        const size = v[key];
        if (!size || seen.has(size)) continue;
        seen.add(size);
        const available = variants.find(u => u[key] === size && u.available);
        groups.push({ size, variantId: (available || v).id, available: !!available });
      }
      return groups;
    }

    async _addToCart(btn) {
      const variantId = btn.dataset.variantId;
      const original = btn.textContent;
      btn.disabled = true;
      btn.textContent = '—';

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: Number(variantId), quantity: 1 }),
        });

        if (!res.ok) throw new Error('add failed');

        btn.textContent = 'Added';

        const cartRes = await fetch('/cart.js');
        if (cartRes.ok) {
          const cart = await cartRes.json();
          document.dispatchEvent(new CustomEvent('cart:update', {
            bubbles: true,
            detail: { resource: cart, data: { source: 'cart-recs' } },
          }));
        }

        setTimeout(() => { btn.textContent = original; btn.disabled = false; }, 2000);
      } catch (_) {
        btn.textContent = 'Error';
        setTimeout(() => { btn.textContent = original; btn.disabled = false; }, 2000);
      }
    }

    _formatMoney(cents) {
      return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
    }

    _resizeImg(url, size) {
      if (!url) return null;
      return url.replace(/(\.[a-zA-Z]+)(\?|$)/, `_${size}x$1$2`);
    }

    _esc(str) {
      return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }
  }

  customElements.define('cart-recs', CartRecs);
})();
